/**
 * Preprocesses markdown content to convert structured data patterns
 * into proper markdown tables for better rendering.
 *
 * Detects patterns like:
 * - Pipe-separated: "ID: value | Code: value | Description: value"
 * - Numbered lists with key-value headers:
 *   1) Code: TEST9
 *      - ID: abc123
 *      - Description: Test item
 * - Numbered lists with simple headers (value only):
 *   1) TEST9
 *      - ID: abc123
 *      - Description: Test item
 */

/**
 * Regex pattern to match pipe-separated key-value pairs
 * Matches lines like: "Key1: Value1 | Key2: Value2 | Key3: Value3"
 */
const PIPE_SEPARATED_PATTERN = /^(.+?:\s*.+?)(\s*\|\s*.+?:\s*.+?)+$/;

/**
 * Regex pattern to match numbered list item headers with key-value pairs
 * Matches lines like: "1) Code: TEST9" or "10) Code: COMP"
 */
const NUMBERED_LIST_HEADER_WITH_KEY_PATTERN = /^\d+\)\s*(.+?):\s*(.+)$/;

/**
 * Regex pattern to match simple numbered list item headers (value only, no key)
 * Matches lines like: "1) TEST9" or "10) COMP" but NOT "1) Code: TEST9"
 */
const NUMBERED_LIST_HEADER_SIMPLE_PATTERN = /^\d+\)\s*([^:\n]+)$/;

/**
 * Default column name for simple numbered list headers
 */
const DEFAULT_HEADER_COLUMN_NAME = 'Name';

/**
 * Regex pattern to match nested bullet points with key-value pairs
 * Matches lines like: "   - ID: abc123", "    - Description: Test", or "- ID: abc123"
 * Leading whitespace is optional to support both indented and non-indented formats
 */
const NESTED_BULLET_PATTERN = /^\s*-\s*(.+?):\s*(.*)$/;

/**
 * Parses a single line of pipe-separated key-value pairs
 * @param line - The line to parse
 * @returns An object with keys and values arrays, or null if not a match
 */
function parsePipeSeparatedLine(
  line: string
): { keys: string[]; values: string[] } | null {
  const trimmedLine = line.trim();

  if (!PIPE_SEPARATED_PATTERN.test(trimmedLine)) {
    return null;
  }

  const pairs = trimmedLine.split(/\s*\|\s*/);
  const keys: string[] = [];
  const values: string[] = [];

  for (const pair of pairs) {
    const colonIndex = pair.indexOf(':');
    if (colonIndex === -1) {
      return null;
    }

    const key = pair.substring(0, colonIndex).trim();
    const value = pair.substring(colonIndex + 1).trim();

    if (!key) {
      return null;
    }

    keys.push(key);
    values.push(value || '—');
  }

  return { keys, values };
}

/**
 * Represents a parsed list item with its properties
 */
interface ListItem {
  headerKey: string;
  headerValue: string;
  properties: Map<string, string>;
}

/**
 * Checks if a line matches any numbered list header pattern
 * @param line - The line to check
 * @returns True if the line is a numbered list header
 */
function isNumberedListHeader(line: string): boolean {
  return (
    NUMBERED_LIST_HEADER_WITH_KEY_PATTERN.test(line) ||
    NUMBERED_LIST_HEADER_SIMPLE_PATTERN.test(line)
  );
}

/**
 * Parses a numbered list block into structured items
 * Supports both key-value headers (e.g., "1) Code: TEST9") and simple headers (e.g., "1) TEST9")
 * @param lines - Array of lines to parse
 * @param startIndex - Starting index in the lines array
 * @returns Parsed list items and the end index
 */
function parseNumberedListBlock(
  lines: string[],
  startIndex: number
): { items: ListItem[]; endIndex: number } {
  const items: ListItem[] = [];
  let i = startIndex;

  while (i < lines.length) {
    const line = lines[i];

    // Try key-value header pattern first (e.g., "1) Code: TEST9")
    const keyValueMatch = NUMBERED_LIST_HEADER_WITH_KEY_PATTERN.exec(line);
    // Try simple header pattern (e.g., "1) TEST9")
    const simpleMatch = NUMBERED_LIST_HEADER_SIMPLE_PATTERN.exec(line);

    if (keyValueMatch) {
      const item: ListItem = {
        headerKey: keyValueMatch[1].trim(),
        headerValue: keyValueMatch[2].trim(),
        properties: new Map(),
      };

      i++;

      // Parse nested bullet points
      while (i < lines.length) {
        const bulletMatch = NESTED_BULLET_PATTERN.exec(lines[i]);
        if (bulletMatch) {
          const key = bulletMatch[1].trim();
          const value = bulletMatch[2].trim() || '—';
          item.properties.set(key, value);
          i++;
        } else if (isNumberedListHeader(lines[i])) {
          // Next numbered item found
          break;
        } else if (lines[i].trim() === '') {
          // Empty line, continue to check for more items
          i++;
        } else {
          // Non-matching line, end of list block
          break;
        }
      }

      items.push(item);
    } else if (simpleMatch) {
      // Simple header without key (e.g., "1) TEST9")
      const item: ListItem = {
        headerKey: DEFAULT_HEADER_COLUMN_NAME,
        headerValue: simpleMatch[1].trim(),
        properties: new Map(),
      };

      i++;

      // Parse nested bullet points
      while (i < lines.length) {
        const bulletMatch = NESTED_BULLET_PATTERN.exec(lines[i]);
        if (bulletMatch) {
          const key = bulletMatch[1].trim();
          const value = bulletMatch[2].trim() || '—';
          item.properties.set(key, value);
          i++;
        } else if (isNumberedListHeader(lines[i])) {
          // Next numbered item found
          break;
        } else if (lines[i].trim() === '') {
          // Empty line, continue to check for more items
          i++;
        } else {
          // Non-matching line, end of list block
          break;
        }
      }

      items.push(item);
    } else {
      break;
    }
  }

  return { items, endIndex: i };
}

/**
 * Escapes pipe characters in cell values to prevent breaking table structure
 * @param value - The cell value to escape
 * @returns Escaped value safe for markdown tables
 */
function escapeTableCellValue(value: string): string {
  return value.replace(/\|/g, '\\|');
}

/**
 * Converts parsed list items into a markdown table
 * @param items - Array of parsed list items
 * @returns Markdown table string
 */
function createTableFromListItems(items: ListItem[]): string {
  if (items.length === 0) {
    return '';
  }

  // Collect all unique property keys across all items
  const allKeys = new Set<string>();
  allKeys.add(items[0].headerKey); // Add header key first (e.g., "Code")

  for (const item of items) {
    const propertyKeys = Array.from(item.properties.keys());
    for (const key of propertyKeys) {
      allKeys.add(key);
    }
  }

  const headers = Array.from(allKeys);
  const headerRow = `| ${headers.join(' | ')} |`;
  const separatorRow = `| ${headers.map(() => '---').join(' | ')} |`;

  const dataRows = items.map((item) => {
    const values = headers.map((header) => {
      if (header === item.headerKey) {
        return escapeTableCellValue(item.headerValue);
      }
      const cellValue = item.properties.get(header) ?? '—';
      return escapeTableCellValue(cellValue);
    });
    return `| ${values.join(' | ')} |`;
  });

  return [headerRow, separatorRow, ...dataRows].join('\n');
}

/**
 * Converts an array of parsed rows into a markdown table
 * @param rows - Array of parsed rows with keys and values
 * @returns Markdown table string
 */
function createMarkdownTable(
  rows: Array<{ keys: string[]; values: string[] }>
): string {
  if (rows.length === 0) {
    return '';
  }

  const headers = rows[0].keys;
  const headerRow = `| ${headers.join(' | ')} |`;
  const separatorRow = `| ${headers.map(() => '---').join(' | ')} |`;
  const dataRows = rows.map((row) => `| ${row.values.join(' | ')} |`);

  return [headerRow, separatorRow, ...dataRows].join('\n');
}

/**
 * Preprocesses markdown content to convert structured data to tables
 * Handles both pipe-separated data and numbered lists with nested bullets
 * @param content - The raw markdown content
 * @returns Processed markdown content with tables
 */
export function preprocessMarkdown(content: string): string {
  const lines = content.split('\n');
  const result: string[] = [];
  let currentTableRows: Array<{ keys: string[]; values: string[] }> = [];
  let expectedKeys: string[] | null = null;
  let i = 0;

  const flushTable = (): void => {
    if (currentTableRows.length > 0) {
      result.push('');
      result.push(createMarkdownTable(currentTableRows));
      result.push('');
      currentTableRows = [];
      expectedKeys = null;
    }
  };

  while (i < lines.length) {
    const line = lines[i];

    // Check for numbered list pattern first (both key-value and simple formats)
    if (isNumberedListHeader(line)) {
      flushTable();
      const { items, endIndex } = parseNumberedListBlock(lines, i);

      if (items.length > 0) {
        result.push('');
        result.push(createTableFromListItems(items));
        result.push('');
        i = endIndex;
        continue;
      }
    }

    // Check for pipe-separated pattern
    const parsed = parsePipeSeparatedLine(line);

    if (parsed) {
      if (expectedKeys === null) {
        expectedKeys = parsed.keys;
        currentTableRows.push(parsed);
      } else if (
        parsed.keys.length === expectedKeys.length &&
        parsed.keys.every((key, idx) => key === expectedKeys![idx])
      ) {
        currentTableRows.push(parsed);
      } else {
        flushTable();
        expectedKeys = parsed.keys;
        currentTableRows.push(parsed);
      }
    } else {
      flushTable();
      result.push(line);
    }

    i++;
  }

  flushTable();

  return result.join('\n').trim();
}
